import type { NumericFormatProps } from 'react-number-format';

export const CURRENCY_SYMBOLS = {
  USD: 'USD ',
  EUR: 'EUR ',
  IRR: 'IRR ',
  IRT: 'IRT ',
  BTC: 'BTC ',
  ETH: 'ETH ',
  USDT: 'USDT ',
  TRX: 'TRX ',
  TRON: 'TRX ',
  USDT_TRON: 'USDT/TRON ',
  USDT_BSC: 'USDT/BSC ',
  VATAN: 'VATAN ',
  BNB: 'BNB ',
  BSC: 'BSC ',
  TRUMP: 'TRUMP ',
  SOL: 'SOL ',
  TRY: 'TRY ',
  SHIB: 'SHIB ',
} as const;

export const CURRENCY_SHORT_SYMBOLS = {
  USD: '$ ',
  EUR: '€ ',
  IRR: '﷼ ',
  IRT: 'ت ',
  BTC: '₿ ',
  ETH: 'Ξ ',
  USDT: '₮ ',
  TRX: 'TRX ',
  TRON: 'TRX ',
  USDT_TRON: 'USDT ',
  USDT_BSC: 'USDT ',
  VATAN: 'VATAN ',
  BNB: 'BNB ',
  BSC: 'BSC ',
  TRUMP: 'TRUMP ',
  SOL: 'SOL ',
  TRY: '₺ ',
  SHIB: 'SHIB ',
} as const;

export const CURRENCY_SPECIFICATIONS: Record<string, Omit<NumericFormatProps, 'prefix'>> = {
  USD: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 2,
    fixedDecimalScale: true,
  },
  EUR: {
    thousandSeparator: '.',
    decimalSeparator: ',',
    decimalScale: 2,
    fixedDecimalScale: true,
  },
  IRR: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 0,
  },
  IRT: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 0,
  },
  BTC: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  ETH: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  USDT: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  USDT_TRON: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  USDT_BSC: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  TRX: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  TRON: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  BNB: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  BSC: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  VATAN: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  TRUMP: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  TRY: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
  SHIB: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 8,
  },
} as const;

type GetNumericFormatPropsOptions = {
  useSymbol?: boolean;
};

/**
 * Helper function to truncate decimals without rounding
 *
 * @example
 * truncateToDecimals(1.23456789, 2) // 1.23
 * truncateToDecimals(1.23456789, 3) // 1.234
 * truncateToDecimals(1.23456789, 4) // 1.2345
 * truncateToDecimals(1.23456789, 5) // 1.23456
 * truncateToDecimals(1.23456789, 6) // 1.234567
 * @param value
 * @param decimals
 * @returns
 */
export const truncateToDecimals = (value: number | string, decimals: number): number => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return 0;
  }
  const factor = Math.pow(10, decimals);
  return Math.floor(num * factor) / factor;
};

export const getNumericFormatProps = (
  currency: keyof typeof CURRENCY_SYMBOLS,
  options: GetNumericFormatPropsOptions = {}
): NumericFormatProps => {
  const { useSymbol = false } = options;
  const prefix = useSymbol ? CURRENCY_SHORT_SYMBOLS[currency] : CURRENCY_SYMBOLS[currency];

  return {
    ...CURRENCY_SPECIFICATIONS[currency],
    prefix,
  };
};
